/**
 * PostHog for the web app: product analytics, session replay and error
 * capture, attributed to the signed-in person and their organization.
 *
 * Off without `VITE_POSTHOG_KEY`. Text is masked in replays (a compliance
 * product must not record what people type or read), pageviews are sent by
 * the router with thread and entity ids replaced by placeholders so paths
 * roll up, and no event carries message content.
 *
 * @module analytics/posthog
 */
import posthog from "posthog-js";
import { clearPostHogStorage, getAnalyticsConsent } from "@repo/shared/analyticsConsent";

export interface PostHogWebConfig {
  readonly key: string;
  readonly host: string;
}

const DEFAULT_HOST = "https://eu.i.posthog.com";
/** Which part of the company an event belongs to: marketing | app | platform. */
export const SURFACE = "app";

/** The client config from Vite's env; `null` when analytics are off. */
export function readPostHogConfig(env: {
  readonly VITE_POSTHOG_KEY?: string | undefined;
  readonly VITE_POSTHOG_HOST?: string | undefined;
}): PostHogWebConfig | null {
  const key = env.VITE_POSTHOG_KEY?.trim();
  if (!key) return null;
  let host = env.VITE_POSTHOG_HOST?.trim() || DEFAULT_HOST;
  while (host.endsWith("/")) host = host.slice(0, -1);
  return { key, host };
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const LONG_TOKEN = /(?<=\/)[A-Za-z0-9_-]{20,}(?=\/|$)/g;

/**
 * A pageview path that groups: `/apex/9c1f…` becomes `/:org/:id`. The first
 * segment is always the organization on org routes, so it is replaced
 * wholesale; ids anywhere else are replaced by `:id`.
 */
export function analyticsPath(pathname: string, orgSegment: string | null): string {
  let path = pathname.replace(UUID, ":id").replace(LONG_TOKEN, ":id");
  if (orgSegment) {
    const prefix = `/${orgSegment}`;
    if (path === prefix || path.startsWith(`${prefix}/`)) path = `/:org${path.slice(prefix.length)}`;
  }
  return path || "/";
}

/**
 * Event properties PostHog attaches that can carry the raw page location:
 * `$current_url`/`$referrer` on every event, `$pathname` on pageviews, and
 * the `href`/`$el_href`/`$external_click_url` autocapture emits. Any of these
 * can hold the one-time pairing credential in the fragment (`/pair#token=…`)
 * or an older `?token=…` query before it would leave the browser.
 */
const URL_PROPERTY_KEYS = [
  "$current_url",
  "$referrer",
  "$pathname",
  "$initial_current_url",
  "$initial_referrer",
  "href",
  "$el_href",
  "$external_click_url",
] as const;

/** Exact query-parameter names that carry a bootstrap/pairing credential. */
const CREDENTIAL_QUERY_PARAM_EXACT = new Set([
  "token",
  "code",
  "key",
  "credential",
  "secret",
  "password",
  "auth",
  "sig",
  "signature",
  "apikey",
  "api_key",
]);

/**
 * True when a query-parameter name carries a credential and must not leave the
 * browser. Covers the exact names above plus any `*token` (e.g. `access_token`,
 * `session_token`, `pairing_token`) and `_secret`/`_credential`/`_key`/`_code`
 * suffixes, without over-matching innocuous names like `encode` or `monkey`.
 */
function isCredentialParam(key: string): boolean {
  const k = key.toLowerCase();
  if (CREDENTIAL_QUERY_PARAM_EXACT.has(k)) return true;
  return (
    k.endsWith("token") ||
    k.endsWith("_secret") ||
    k.endsWith("_credential") ||
    k.endsWith("_key") ||
    k.endsWith("_code") ||
    k.endsWith("_password")
  );
}

function stripCredentials(url: URL): void {
  url.hash = "";
  for (const key of [...url.searchParams.keys()]) {
    if (isCredentialParam(key)) url.searchParams.delete(key);
  }
}

/**
 * Strip the fragment and any credential query parameter from a URL before it
 * leaves the browser. The one-time pairing credential lives in the fragment
 * (`/pair#token=…`); older links pass `?token=…`. PostHog needs neither.
 * Handles absolute URLs, relative paths (`$pathname`) and unparseable values.
 */
export function sanitizeAnalyticsUrl(value: string): string {
  try {
    const url = new URL(value);
    stripCredentials(url);
    return url.toString();
  } catch {
    // Not absolute: treat as a relative path/query/fragment.
  }
  try {
    const url = new URL(value, "http://relative.invalid");
    stripCredentials(url);
    return `${url.pathname}${url.search}`;
  } catch {
    return value.split("#", 1)[0] ?? "";
  }
}

/**
 * Scrub the address-bar URL captured inside a session-recording snapshot.
 * rrweb Meta events (`type === 4`) carry `data.href` — the full location,
 * including the `/pair#token=…` credential — which top-level property
 * sanitization does not reach. Walk the snapshot array and sanitize any
 * `href`/`url` string on each event's `data`.
 */
export function sanitizeSnapshotData(snapshot: unknown): void {
  if (!Array.isArray(snapshot)) return;
  for (const item of snapshot) {
    const data = (item as { data?: Record<string, unknown> } | null)?.data;
    if (!data || typeof data !== "object") continue;
    if (typeof data.href === "string") data.href = sanitizeAnalyticsUrl(data.href);
    if (typeof data.url === "string") data.url = sanitizeAnalyticsUrl(data.url);
  }
}

/** Sanitize every URL-bearing property in place, returning the same object. */
export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  for (const key of URL_PROPERTY_KEYS) {
    const value = properties[key];
    if (typeof value === "string") properties[key] = sanitizeAnalyticsUrl(value);
  }
  // Session-recording payloads hide the address-bar URL inside the snapshot.
  if ("$snapshot_data" in properties) sanitizeSnapshotData(properties["$snapshot_data"]);
  return properties;
}

let started = false;

export function startPostHog(config: PostHogWebConfig): void {
  if (getAnalyticsConsent() !== "accepted") return;
  if (started) {
    if (posthog.has_opted_out_capturing()) {
      posthog.set_config({ advanced_disable_flags: false, disable_persistence: false });
      posthog.opt_in_capturing();
      registerSurface();
      posthog.startSessionRecording();
    }
    return;
  }
  started = true;
  posthog.init(config.key, {
    api_host: config.host,
    defaults: "2025-05-24",
    cookie_expiration: 180,
    cross_subdomain_cookie: false,
    // The router reports pageviews with grouped paths; see analyticsPath.
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: true,
    autocapture: false,
    person_profiles: "identified_only",
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: { maskAllInputs: true, maskTextSelector: "*" },
    // Last line before transport: no captured URL property may carry the
    // pairing credential (fragment or `?token=`) to the third-party endpoint.
    before_send: (event) => {
      if (getAnalyticsConsent() !== "accepted") return null;
      if (event?.properties) sanitizeAnalyticsProperties(event.properties);
      return event;
    },
  });
  posthog.opt_in_capturing();
  registerSurface();
}

export function stopPostHog(projectKey?: string): void {
  if (started) {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
    // Flag requests carry identifiers but bypass the event before_send hook.
    posthog.set_config({ advanced_disable_flags: true, disable_persistence: true });
    // Storage removal alone leaves identity and groups in the SDK singleton.
    posthog.reset(true);
    // reset() also clears the SDK consent marker; preserve withdrawal.
    posthog.opt_out_capturing();
  }
  clearPostHogStorage(projectKey);
}

// Same workspace as the marketing site and the servers; the surface property
// is what dashboards split on (see docs/operations/posthog.md). A reset wipes
// registered properties, so this runs after every reset as well as at start.
function registerSurface(): void {
  posthog.register({ surface: SURFACE, service: "amoreg-web" });
}

export function capturePageview(path: string): void {
  if (!started || getAnalyticsConsent() !== "accepted") return;
  posthog.capture("$pageview", { $current_url: `${window.location.origin}${path}` });
}

/** Who is using the app and which organization they are working in. */
export function identifyViewer(viewer: {
  readonly userId: string | null;
  readonly email: string | null;
  readonly organizationId: string | null;
}): void {
  if (!started || getAnalyticsConsent() !== "accepted") return;
  if (viewer.userId) {
    posthog.identify(viewer.userId, viewer.email ? { email: viewer.email } : undefined);
  }
  if (viewer.organizationId) {
    posthog.group("organization", viewer.organizationId);
  }
}

export function resetViewer(): void {
  if (!started || getAnalyticsConsent() !== "accepted") return;
  posthog.reset();
  registerSurface();
}
